import React, { useState, useEffect } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { Heart, MapPin, Calendar, Users, Star, ChevronRight, Clock, MessageCircle, ArrowRight, Mail, Phone, Instagram, Facebook, Twitter } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import LoginModal from './LoginModal';
import PublicRegistrationModal from './PublicRegistrationModal';
import EventRegistrationModal from './EventRegistrationModal';
import MessaggeriaLoginModal from './MessaggeriaLoginModal';
import { supabase } from '../lib/supabase';
import type { Tables } from '../lib/types';

interface EventWithLocation extends Tables<'events'> {
  locationImage?: string | null;
  locationImagePositionX?: number;
  locationImagePositionY?: number;
}

const LoginPage = () => {
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showRegistrationModal, setShowRegistrationModal] = useState(false);
  const [showEventRegistration, setShowEventRegistration] = useState(false);
  const [showMessaggeria, setShowMessaggeria] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<EventWithLocation | null>(null);
  const [upcomingEvents, setUpcomingEvents] = useState<EventWithLocation[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [registrationStatus, setRegistrationStatus] = useState<'success' | 'cancelled' | null>(null);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    loadUpcomingEvents();
    const params = new URLSearchParams(window.location.search);
    const regStatus = params.get('registration');
    if (regStatus === 'success') {
      setRegistrationStatus('success');
      window.history.replaceState({}, '', window.location.pathname);
    } else if (regStatus === 'cancelled') {
      setRegistrationStatus('cancelled');
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  useEffect(() => {
    if (upcomingEvents.length > 3) {
      const totalSlides = Math.ceil(upcomingEvents.length / 3);
      const interval = setInterval(() => {
        setCurrentIndex((prevIndex) => (prevIndex + 1) % totalSlides);
      }, 5000);

      return () => clearInterval(interval);
    }
  }, [upcomingEvents.length]);

  const loadUpcomingEvents = async () => {
    try {
      const { data, error } = await supabase
        .from('events')
        .select('*')
        .gte('date', new Date().toISOString().split('T')[0])
        .in('status', ['in programma', 'posti limitati', 'quasi completo', 'sold-out + lista attesa'])
        .order('date', { ascending: true });

      if (error) throw error;

      // Load locations to get images for events
      const { data: locations } = await supabase
        .from('locations')
        .select('name, image_url, image_position_x, image_position_y');

      const locationImagesMap = new Map(
        locations?.map(loc => [loc.name, {
          url: loc.image_url,
          positionX: loc.image_position_x ?? 50,
          positionY: loc.image_position_y ?? 50
        }]) || []
      );

      // Map events with location images
      const eventsWithImages = (data || []).map(event => {
        const locationData = locationImagesMap.get(event.location);
        return {
          ...event,
          locationImage: locationData?.url || null,
          locationImagePositionX: locationData?.positionX ?? 50,
          locationImagePositionY: locationData?.positionY ?? 50
        };
      });

      setUpcomingEvents(eventsWithImages);
    } catch (error) {
      console.error('Error loading events:', error);
    }
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('it-IT', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  if (user) {
    return <Navigate to="/dashboard" />;
  }

  const openEventRegistration = (evento: EventWithLocation) => {
    setSelectedEvent(evento);
    setShowEventRegistration(true);
  };

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen bg-[#FFF5F7]">
      <header className="fixed top-0 left-0 right-0 z-50 bg-gradient-to-r from-[#D91C5C] to-[#8B1538] shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-2">
              <Heart className="h-6 w-6 text-white fill-white" />
              <span className="text-xl font-bold text-white">FlashCrush</span>
            </div>
            <nav className="hidden md:flex items-center gap-8">
              <button onClick={() => scrollToSection('come-funziona')} className="text-white hover:text-white/80 transition-colors">Come Funziona</button>
              <button onClick={() => scrollToSection('eventi')} className="text-white hover:text-white/80 transition-colors">Eventi</button>
              <button onClick={() => scrollToSection('testimonianze')} className="text-white hover:text-white/80 transition-colors">Testimonianze</button>
            </nav>
            <div className="flex items-center gap-3">
              <button
                onClick={() => setShowMessaggeria(true)}
                className="bg-white/20 hover:bg-white/30 text-white px-6 py-2 rounded-lg font-medium transition-all border border-white/40"
              >
                Messaggeria
              </button>
              <button
                onClick={() => navigate('/heartlink')}
                className="bg-white/20 hover:bg-white/30 text-white px-6 py-2 rounded-lg font-medium transition-all border border-white/40"
              >
                Speed Date
              </button>
            </div>
          </div>
        </div>
      </header>

      <section className="pt-32 pb-24 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="text-center lg:text-left">
              <h1 className="text-5xl md:text-6xl font-bold mb-6">
                <span className="text-[#D91C5C]">Trova la Tua</span>
                <br />
                <span className="text-gray-900">Anima Gemella Stasera</span>
              </h1>

              <p className="text-lg md:text-xl text-gray-600 mb-10 leading-relaxed">
                Partecipa ai nostri eventi esclusivi di speed dating dove le connessioni significative nascono in pochi minuti. Incontra single compatibili in un ambiente divertente, sicuro e senza pressioni.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start mb-8">
                <button
                  onClick={() => scrollToSection('eventi')}
                  className="bg-[#D91C5C] hover:bg-[#C01850] text-white px-8 py-3.5 rounded-lg font-semibold transition-all inline-flex items-center justify-center gap-2"
                >
                  Eventi in Programma
                  <ChevronRight className="h-5 w-5" />
                </button>
                <button
                  onClick={() => setShowRegistrationModal(true)}
                  className="bg-white hover:bg-gray-50 text-[#D91C5C] px-8 py-3.5 rounded-lg font-semibold border-2 border-[#D91C5C] transition-all inline-flex items-center justify-center gap-2"
                >
                  Registrati Ora
                  <ArrowRight className="h-5 w-5" />
                </button>
              </div>
            </div>

            <div className="relative">
              <div className="rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src="/dating.jpg"
                  alt="Speed dating event"
                  className="w-full h-[500px] object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 bg-white rounded-xl shadow-lg p-4 border border-gray-100">
                <div className="flex items-center gap-3">
                  <Heart className="h-8 w-8 text-[#D91C5C] fill-[#D91C5C]" />
                  <div>
                    <div className="text-2xl font-bold text-gray-900">10,000+</div>
                    <div className="text-sm text-gray-600">Match Creati</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-20">

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-5xl mx-auto">
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
              <div className="text-[1.7rem] font-bold text-[#D91C5C] mb-1">Valutazione 4.9/5</div>
              <div className="text-gray-600 text-[0.79rem]">Recensioni Clienti</div>
            </div>
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
              <div className="text-[1.7rem] font-bold text-[#D91C5C] mb-1">10,000+ Match</div>
              <div className="text-gray-600 text-[0.79rem]">Connessioni Create</div>
            </div>
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
              <div className="text-[1.7rem] font-bold text-[#D91C5C] mb-1">500+ Eventi</div>
              <div className="text-gray-600 text-[0.79rem]">Organizzati</div>
            </div>
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
              <div className="text-[1.7rem] font-bold text-[#D91C5C] mb-1">28% Tasso di Successo</div>
              <div className="text-gray-600 text-[0.79rem]">Match a Lungo Termine</div>
            </div>
          </div>
          </div>
        </div>
      </section>

      <section id="come-funziona" className="py-20 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Come Funziona lo Speed Dating
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Il nostro semplice processo in quattro fasi rende gli incontri divertenti ed efficienti.
            </p>
          </div>

          <div className="mb-12 rounded-2xl overflow-hidden shadow-lg">
            <img
              src="https://images.pexels.com/photos/1024993/pexels-photo-1024993.jpeg?auto=compress&cs=tinysrgb&w=1200"
              alt="Gruppo di persone che socializzano a un evento"
              className="w-full h-64 object-cover"
            />
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 relative">
            <div className="absolute top-24 left-[12.5%] right-[12.5%] h-0.5 border-t-2 border-dashed border-gray-300 hidden lg:block"></div>

            <div className="relative bg-white rounded-xl p-8 shadow-sm border border-gray-100">
              <div className="flex justify-center mb-6">
                <div className="w-20 h-20 rounded-full bg-[#D91C5C] flex items-center justify-center">
                  <Calendar className="h-10 w-10 text-white" />
                </div>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3 text-center">Registrati a un Evento</h3>
              <p className="text-gray-600 text-center leading-relaxed">
                Sfoglia i nostri eventi in programma e seleziona quello che si adatta al tuo calendario. Registrati online per assicurarti il tuo posto.
              </p>
            </div>

            <div className="relative bg-white rounded-xl p-8 shadow-sm border border-gray-100">
              <div className="flex justify-center mb-6">
                <div className="w-20 h-20 rounded-full bg-[#D91C5C] flex items-center justify-center">
                  <Clock className="h-10 w-10 text-white" />
                </div>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3 text-center">Partecipa all'Evento</h3>
              <p className="text-gray-600 text-center leading-relaxed">
                Arriva al locale e goditi una serie di appuntamenti di 5 minuti con altri single. È veloce, divertente e senza pressioni!
              </p>
            </div>

            <div className="relative bg-white rounded-xl p-8 shadow-sm border border-gray-100">
              <div className="flex justify-center mb-6">
                <div className="w-20 h-20 rounded-full bg-[#D91C5C] flex items-center justify-center">
                  <Heart className="h-10 w-10 text-white" />
                </div>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3 text-center">Segna i Tuoi Match</h3>
              <p className="text-gray-600 text-center leading-relaxed">
                Dopo ogni mini-appuntamento, indica se ti piacerebbe rivederti. Il nostro sistema terrà traccia delle tue preferenze.
              </p>
            </div>

            <div className="relative bg-white rounded-xl p-8 shadow-sm border border-gray-100">
              <div className="flex justify-center mb-6">
                <div className="w-20 h-20 rounded-full bg-[#D91C5C] flex items-center justify-center">
                  <MessageCircle className="h-10 w-10 text-white" />
                </div>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3 text-center">Connettiti con i Match</h3>
              <p className="text-gray-600 text-center leading-relaxed">
                Quando due persone mostrano interesse reciproco, li notificheremo entrambi e attiveremo la messaggistica per aiutarti a connettervi ulteriormente.
              </p>
            </div>
          </div>

          <div className="text-center mt-12">
            <button
              onClick={() => setShowLoginModal(true)}
              className="bg-[#D91C5C] hover:bg-[#C01850] text-white px-8 py-3 rounded-lg font-semibold transition-all"
            >
              Inizia Oggi
            </button>
          </div>
        </div>
      </section>

      <section id="eventi" className="py-20 px-4 bg-[#FFF5F7]">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Prossimi Eventi Speed Dating
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Trova la tua anima gemella a uno dei nostri emozionanti eventi. Registrati ora per assicurarti il tuo posto!
            </p>
          </div>

          {upcomingEvents.length > 0 ? (
            <div className="relative">
              <div className="overflow-hidden">
                <div
                  className="flex transition-transform duration-700 ease-in-out"
                  style={{ transform: `translateX(-${currentIndex * 100}%)` }}
                >
                  {Array.from({ length: Math.ceil(upcomingEvents.length / 3) }).map((_, slideIndex) => (
                    <div key={slideIndex} className="w-full flex-shrink-0">
                      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 px-2">
                        {upcomingEvents.slice(slideIndex * 3, slideIndex * 3 + 3).map((evento, idx) => {
                          const availableSpots = evento.max_participants - (evento.participant_count || 0);

                          const participantCount = evento.participant_count || 0;
                          const maxParticipants = evento.max_participants;
                          const percentageFull = (participantCount / maxParticipants) * 100;

                          let ribbonText = '';
                          let ribbonColor = '';

                          if (evento.status === 'sold-out + lista attesa') {
                            ribbonText = 'SOLD-OUT + LISTA ATTESA';
                            ribbonColor = 'bg-red-600';
                          } else if (evento.status === 'quasi completo') {
                            ribbonText = 'QUASI COMPLETO';
                            ribbonColor = 'bg-orange-500';
                          } else if (evento.status === 'posti limitati') {
                            ribbonText = 'POSTI LIMITATI';
                            ribbonColor = 'bg-yellow-500';
                          } else if (evento.status === 'in programma') {
                            ribbonText = 'IN PROGRAMMA';
                            ribbonColor = 'bg-green-600';
                          }

                          return (
                            <div key={evento.id} className="relative bg-white rounded-xl overflow-hidden shadow-sm border border-gray-100 hover:shadow-md transition-all">
                              {ribbonText && (
                                <div className="absolute top-0 right-0 z-10 overflow-hidden w-40 h-40 pointer-events-none">
                                  <div className={`absolute top-8 ${ribbonText === 'SOLD-OUT + LISTA ATTESA' ? '-right-14' : ribbonText === 'IN PROGRAMMA' || ribbonText === 'POSTI LIMITATI' ? '-right-10' : '-right-11'} ${ribbonColor} text-white ${ribbonText === 'SOLD-OUT + LISTA ATTESA' ? 'text-[0.55rem]' : 'text-[0.65rem]'} font-bold ${ribbonText === 'SOLD-OUT + LISTA ATTESA' ? 'px-16' : ribbonText === 'IN PROGRAMMA' || ribbonText === 'POSTI LIMITATI' ? 'px-12' : 'px-11'} py-1.5 rotate-45 shadow-md whitespace-nowrap`}>
                                    {ribbonText}
                                  </div>
                                </div>
                              )}

                              <div className="h-48 overflow-hidden bg-gradient-to-br from-rose-500 to-pink-600 relative">
                                {((evento as any).image_url || evento.locationImage) ? (
                                  <img
                                    src={(evento as any).image_url || evento.locationImage}
                                    alt={evento.location}
                                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                                    style={!(evento as any).image_url && evento.locationImage ? {
                                      objectPosition: `${evento.locationImagePositionX ?? 50}% ${evento.locationImagePositionY ?? 50}%`
                                    } : undefined}
                                  />
                                ) : (
                                  <div className="absolute inset-0 flex items-center justify-center">
                                    <Calendar className="h-20 w-20 text-white/30" />
                                  </div>
                                )}
                              </div>
                              <div className="bg-[#D91C5C] text-white p-4">
                                <h3 className="text-xl font-bold">{evento.name}</h3>
                              </div>
                              <div className="p-6">
                                <div className="space-y-3 mb-4">
                                  <div className="flex items-center gap-3 text-gray-700">
                                    <Calendar className="h-5 w-5 text-[#D91C5C]" />
                                    <span>{formatDate(evento.date)}</span>
                                  </div>
                                  <div className="flex items-center gap-3 text-gray-700">
                                    <Clock className="h-5 w-5 text-[#D91C5C]" />
                                    <span>{evento.time}</span>
                                  </div>
                                  <div className="flex items-center gap-3 text-gray-700">
                                    <MapPin className="h-5 w-5 text-[#D91C5C]" />
                                    <span>{evento.location}, {evento.city}</span>
                                  </div>
                                  <div className="flex items-center gap-3 text-gray-700">
                                    <Users className="h-5 w-5 text-[#D91C5C]" />
                                    <span>{availableSpots} posti rimanenti</span>
                                  </div>
                                </div>
                                <p className="text-gray-600 mb-6 text-sm">
                                  {availableSpots > 10
                                    ? `Unisciti a noi per una serata indimenticabile di speed dating. Fascia d'età: ${evento.age_range}. Incontr...`
                                    : availableSpots > 5
                                    ? `Posti limitati per questo evento speciale! Fascia d'età: ${evento.age_range}. Non perdere l'occasione di...`
                                    : availableSpots > 0
                                    ? `Ultimi posti disponibili! Prenota subito per la fascia d'età ${evento.age_range}. Affrettati prima che...`
                                    : `Evento completo. Iscriviti alla lista d'attesa per la fascia d'età ${evento.age_range}...`
                                  }
                                </p>
                                <div className="flex justify-end">
                                  <button
                                    onClick={() => openEventRegistration(evento)}
                                    className="bg-[#D91C5C] hover:bg-[#C01850] text-white px-6 py-2 rounded-lg font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                                    disabled={availableSpots === 0}
                                  >
                                    {availableSpots > 0 ? 'Registrati' : 'Sold Out'}
                                  </button>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {Math.ceil(upcomingEvents.length / 3) > 1 && (
                <div className="flex justify-center gap-2 mt-8">
                  {Array.from({ length: Math.ceil(upcomingEvents.length / 3) }).map((_, idx) => (
                    <button
                      key={idx}
                      onClick={() => setCurrentIndex(idx)}
                      className={`h-3 rounded-full transition-all ${
                        idx === currentIndex
                          ? 'w-8 bg-[#D91C5C]'
                          : 'w-3 bg-gray-300 hover:bg-gray-400'
                      }`}
                      aria-label={`Vai alla pagina ${idx + 1}`}
                    />
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-12">
              <Calendar className="h-16 w-16 mx-auto mb-4 text-gray-300" />
              <p className="text-gray-600 text-lg">Nessun evento in programma al momento</p>
              <p className="text-gray-500 mt-2">Torna presto per nuovi eventi!</p>
            </div>
          )}

          <div className="text-center mt-12">
            <button
              onClick={() => setShowLoginModal(true)}
              className="bg-[#D91C5C] hover:bg-[#C01850] text-white px-8 py-3 rounded-lg font-semibold transition-all"
            >
              Vedi Tutti gli Eventi
            </button>
          </div>
        </div>
      </section>

      <section id="testimonianze" className="py-20 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Storie di Successo
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Leggi le vere connessioni nate ai nostri eventi.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                name: 'Sara & Marco',
                city: 'Milano, IT',
                text: 'Ci siamo incontrati a un evento SpeedMatch l\'anno scorso e abbiamo fatto subito click! Ora siamo fidanzati e stiamo pianificando il nostro matrimonio. Grazie per averci fatto incontrare!',
                rating: 5,
                image: 'https://images.pexels.com/photos/3184325/pexels-photo-3184325.jpeg?auto=compress&cs=tinysrgb&w=150&h=150&fit=crop'
              },
              {
                name: 'Giovanni Rossi',
                city: 'Roma, IT',
                text: 'Ero scettico sullo speed dating, ma gli eventi ben organizzati e la qualità degli incontri mi hanno fatto cambiare idea. Ho conosciuto persone fantastiche!',
                rating: 4,
                image: 'https://images.pexels.com/photos/2379004/pexels-photo-2379004.jpeg?auto=compress&cs=tinysrgb&w=150&h=150&fit=crop'
              },
              {
                name: 'Michele & Davide',
                city: 'Firenze, IT',
                text: 'Dopo aver provato le app di dating senza successo, gli eventi SpeedMatch sono stati una boccata d\'aria fresca. L\'interazione faccia a faccia ha fatto la differenza.',
                rating: 5,
                image: 'https://images.pexels.com/photos/3184291/pexels-photo-3184291.jpeg?auto=compress&cs=tinysrgb&w=150&h=150&fit=crop'
              },
            ].map((story, idx) => (
              <div key={idx} className="bg-white rounded-xl p-8 shadow-sm border border-gray-100">
                <div className="flex gap-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star
                      key={i}
                      className={`h-5 w-5 ${i < story.rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'}`}
                    />
                  ))}
                </div>
                <p className="text-gray-700 mb-6 leading-relaxed italic">"{story.text}"</p>
                <div className="flex items-center gap-3">
                  <img
                    src={story.image}
                    alt={story.name}
                    className="w-12 h-12 rounded-full object-cover"
                  />
                  <div>
                    <div className="font-bold text-gray-900">{story.name}</div>
                    <div className="text-sm text-gray-600">{story.city}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <footer className="bg-[#A81444] text-white py-12 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <Heart className="h-6 w-6 fill-white" />
                <span className="text-xl font-bold">FlashCrush</span>
              </div>
              <p className="text-white/90 text-sm leading-relaxed mb-4">
                Creiamo connessioni significative in pochi minuti. Unisciti alla nostra comunità di single in cerca dell'amore.
              </p>
              <div className="flex gap-3">
                <a href="https://instagram.com" target="_blank" rel="noopener noreferrer" className="hover:text-white/70 transition-colors">
                  <Instagram className="h-5 w-5" />
                </a>
                <a href="https://facebook.com" target="_blank" rel="noopener noreferrer" className="hover:text-white/70 transition-colors">
                  <Facebook className="h-5 w-5" />
                </a>
                <a href="https://twitter.com" target="_blank" rel="noopener noreferrer" className="hover:text-white/70 transition-colors">
                  <Twitter className="h-5 w-5" />
                </a>
              </div>
            </div>

            <div>
              <h4 className="font-bold mb-4">Eventi</h4>
              <ul className="space-y-2 text-sm">
                <li><button onClick={() => scrollToSection('eventi')} className="hover:text-white/70 transition-colors">Eventi in Programma</button></li>
                <li><button onClick={() => setShowLoginModal(true)} className="hover:text-white/70 transition-colors">Registrati all'Evento</button></li>
                <li><button onClick={() => scrollToSection('eventi')} className="hover:text-white/70 transition-colors">Località Eventi</button></li>
                <li><a href="#" className="hover:text-white/70 transition-colors">FAQ Eventi</a></li>
              </ul>
            </div>

            <div>
              <h4 className="font-bold mb-4">Azienda</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="#" className="hover:text-white/70 transition-colors">Chi Siamo</a></li>
                <li><button onClick={() => setShowLoginModal(true)} className="hover:text-white/70 transition-colors">CRM</button></li>
                <li><button onClick={() => navigate('/affiliati')} className="hover:text-white/70 transition-colors">AFFILIATI</button></li>
                <li><button onClick={() => navigate('/lavora-con-noi')} className="hover:text-white/70 transition-colors">Lavora con Noi</button></li>
              </ul>
            </div>

            <div>
              <h4 className="font-bold mb-4">Supporto</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="mailto:info@speedmatch.it" className="hover:text-white/70 transition-colors">Contattaci</a></li>
                <li><a href="#" className="hover:text-white/70 transition-colors">Privacy Policy</a></li>
                <li><a href="#" className="hover:text-white/70 transition-colors">Termini di Servizio</a></li>
                <li><a href="#" className="hover:text-white/70 transition-colors">Centro Assistenza</a></li>
              </ul>
            </div>
          </div>

          <div className="border-t border-white/20 pt-6 text-center text-sm text-white/80">
            <p>&copy; 2026 FlashCrush. Tutti i diritti riservati.</p>
          </div>
        </div>
      </footer>

      {registrationStatus && (
        <div className={`fixed top-20 left-1/2 -translate-x-1/2 z-50 px-6 py-4 rounded-xl shadow-2xl max-w-md w-full mx-4 ${
          registrationStatus === 'success'
            ? 'bg-green-50 border border-green-200'
            : 'bg-yellow-50 border border-yellow-200'
        }`}>
          <div className="flex items-start justify-between gap-3">
            <div>
              <h3 className={`font-bold ${registrationStatus === 'success' ? 'text-green-800' : 'text-yellow-800'}`}>
                {registrationStatus === 'success' ? 'Pagamento confermato!' : 'Pagamento annullato'}
              </h3>
              <p className={`text-sm mt-1 ${registrationStatus === 'success' ? 'text-green-700' : 'text-yellow-700'}`}>
                {registrationStatus === 'success'
                  ? 'La tua registrazione all\'evento e\' stata completata con successo. Riceverai una conferma via email.'
                  : 'Il pagamento e\' stato annullato. Puoi riprovare quando vuoi.'}
              </p>
            </div>
            <button
              onClick={() => setRegistrationStatus(null)}
              className={`text-sm font-medium flex-shrink-0 ${
                registrationStatus === 'success' ? 'text-green-600 hover:text-green-800' : 'text-yellow-600 hover:text-yellow-800'
              }`}
            >
              Chiudi
            </button>
          </div>
        </div>
      )}

      <LoginModal isOpen={showLoginModal} onClose={() => setShowLoginModal(false)} />
      <PublicRegistrationModal isOpen={showRegistrationModal} onClose={() => setShowRegistrationModal(false)} />
      <EventRegistrationModal
        isOpen={showEventRegistration}
        onClose={() => { setShowEventRegistration(false); setSelectedEvent(null); }}
        event={selectedEvent}
      />
      <MessaggeriaLoginModal isOpen={showMessaggeria} onClose={() => setShowMessaggeria(false)} />
    </div>
  );
};

export default LoginPage;
